import type { PlanStatus } from '@models/lib/tenant';

/**
 * Acceso de un tenant según su plan: una única fuente de verdad para el guard
 * del admin (`planActiveGuard`) y para las funciones públicas del widget
 * embebible (feature 513, R106–R111).
 *
 * Pura y sin dependencias de runtime (solo `import type`), para que la puedan
 * importar las functions por ruta profunda (`@utils/lib/plan-access`).
 */

/** Estados que dejan usar el panel, con un banner de pago pendiente. */
export type DegradedPlanStatus = Extract<
  PlanStatus,
  'trial_expired' | 'pending_subscription' | 'pending_payment' | 'past_due'
>;

export type PlanAccess =
  | { access: 'blocked'; status: 'cancelled' | 'suspended' | 'expired' }
  | { access: 'degraded'; status: DegradedPlanStatus }
  | { access: 'ok' };

/** Campos del documento del tenant que decide el acceso. */
export interface PlanAccessInput {
  planStatus?: string | null;
  trialEndsAt?: unknown;
  planExpiresAt?: unknown;
}

const HARD_BLOCKED: readonly string[] = ['cancelled', 'suspended'];

const DEGRADED: readonly DegradedPlanStatus[] = [
  'trial_expired',
  'pending_subscription',
  'pending_payment',
  'past_due',
];

/**
 * Fecha de un campo del tenant: `Timestamp` de cualquiera de los dos SDK (se
 * reconoce por `toDate`), `Date`, cadena o número. Misma conversión que hacía el
 * guard: si no es una fecha válida, las comparaciones dan `false` y la regla no
 * se aplica.
 */
function toDate(value: unknown): Date {
  const withToDate = value as { toDate?: () => Date };
  return withToDate?.toDate ? withToDate.toDate() : new Date(value as string | number | Date);
}

/**
 * Clasifica el acceso por plan. Gana la primera regla que se cumple, en este
 * orden (el que tenía `planActiveGuard`):
 *
 * 1. `cancelled` o `suspended` → `blocked` con ese estado.
 * 2. `trial_expired`, `pending_subscription`, `pending_payment` o `past_due`
 *    → `degraded`.
 * 3. `expired` (valor legado, fuera de `PlanStatus`) → `blocked`.
 * 4. `trial` con `trialEndsAt` anterior a `now` → `degraded` (`trial_expired`).
 * 5. `planExpiresAt` anterior a `now` → `blocked` (`expired`).
 * 6. Resto, incluido `planStatus` ausente → `ok`.
 */
export function classifyPlanAccess(
  tenant: PlanAccessInput | null | undefined,
  now: Date = new Date(),
): PlanAccess {
  const planStatus: string = tenant?.planStatus ?? 'active';

  if (HARD_BLOCKED.includes(planStatus)) {
    return { access: 'blocked', status: planStatus as 'cancelled' | 'suspended' };
  }

  if ((DEGRADED as readonly string[]).includes(planStatus)) {
    return { access: 'degraded', status: planStatus as DegradedPlanStatus };
  }

  if (planStatus === 'expired') {
    return { access: 'blocked', status: 'expired' };
  }

  if (planStatus === 'trial') {
    const trialEndsAt = tenant?.trialEndsAt;
    if (trialEndsAt && toDate(trialEndsAt) < now) {
      return { access: 'degraded', status: 'trial_expired' };
    }
  }

  const planExpiresAt = tenant?.planExpiresAt;
  if (planExpiresAt && toDate(planExpiresAt) < now) {
    return { access: 'blocked', status: 'expired' };
  }

  return { access: 'ok' };
}
